import numeral from 'numeral';

import {
  setValue,
  useOrderbookStore as wsStore,
} from 'global/MainLayout/SideWidget/Watchlist/Orderbook/store';

import { useRouter } from 'next/router';
import { Common } from '@stockbit/ui';
import { renderTags } from 'utils/tags';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { useLivePriceSingleReceiver } from 'global/Datafeed/UseLivePrice/useLivePriceSingleReceiver';
import SymbolLink from 'global/MainLayout/SideWidget/SymbolLink';
import CloseIcon from './CloseIcon';
import DeleteOption from './DeleteOption';
import slice from '../Orderbook/slice';

const { Flex } = Common;

type RowWatchlistProps = {
  data: any;
  index: number;
  watchlistId: string;
  closedRow: number;
  setClosedRow: (i: number) => void;
};

const RowWatchlist = ({
  data,
  index,
  watchlistId,
  closedRow,
  setClosedRow,
}: RowWatchlistProps) => {
  const dispatch = useDispatch();
  const { push, query, pathname } = useRouter();
  const routerSymbol = Array.isArray(query.symbol)
    ? query.symbol[0]
    : query.symbol;
  const isInChartbitPage = pathname.includes('chartbit');

  const { symbol, last, change, percent } = data;
  const liveprice = wsStore(({ liveprice: state }) => state?.[symbol]);
  const lastPrice = liveprice?.lastprice || last;
  const lastChange = liveprice?.change || change;
  const lastPercent = liveprice?.percentage_change || percent;

  const getChangeCls = (priceChanges) => {
    if (priceChanges === 0) {
      return 'neutral';
    }

    if (priceChanges < 0) {
      return 'down';
    }

    return 'up';
  };

  const renderChange = (dataChanges) => {
    const { change: dataLastChange, percent: dataLastPercent } = dataChanges;
    let format = '0.00';
    let percentNumeral = numeral(dataLastPercent);
    if (percentNumeral.value() > 100) {
      percentNumeral = percentNumeral.divide(100);
    }

    let formattedPercent = percentNumeral.format(format);
    const formattedChange = numeral(dataLastChange).format('0,0[.]0[0]');
    const formattedPercentNumber = Number(formattedPercent);

    if (formattedPercentNumber < 0) {
      format = `-${format}`;
    } else if (formattedPercentNumber > 0) {
      format = `+${format}`;
    }

    formattedPercent = percentNumeral.format(format);

    return (
      <td
        style={{ position: 'relative' }}
        className={getChangeCls(formattedPercentNumber)}
      >
        <SymbolLink symbol={symbol} isPreventDefault>
          <div>{`${formattedChange} (${formattedPercent}%)`}</div>
        </SymbolLink>
        <CloseIcon
          onClick={(e) => {
            e.stopPropagation();
            setClosedRow(index);
          }}
        />
      </td>
    );
  };

  const renderCompany = () => {
    const isHovered = closedRow === index;
    return (
      <td>
        <div style={{ position: 'relative' }}>
          <div
            style={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: isHovered ? 'hidden' : 'unset',
              minWidth: 0,
            }}
          >
            <SymbolLink symbol={symbol} isPreventDefault>
              <Flex align="center">
                {symbol}
                {renderTags(data, false)}
              </Flex>
            </SymbolLink>
          </div>
          {isHovered ? (
            <DeleteOption
              symbol={symbol}
              id={data.id}
              onClick={(e) => e.stopPropagation()}
              onCancel={() => setClosedRow(undefined)}
              watchlistId={watchlistId}
            />
          ) : null}
        </div>
      </td>
    );
  };

  const livePriceOnMessage = useCallback((message) => {
    setValue({
      key: 'liveprice',
      symbol: message.stockCode,
      type: 'update_general_pricedata',
      value: { ...message, symbol: message.stockCode },
    });
  }, []);
  useLivePriceSingleReceiver(
    symbol,
    'sidebar_watchlist',
    livePriceOnMessage,
  );

  return (
    <tr
      className={routerSymbol === symbol ? 'active' : null}
      onClick={(e) => {
        e.stopPropagation();
        dispatch(slice.actions.setSymbol({ symbol }));
        push(
          `/symbol/${symbol}${isInChartbitPage ? '/chartbit' : ''}`,
          undefined,
          {
            shallow: isInChartbitPage,
          },
        );
      }}
    >
      {renderCompany()}
      <td>
        <SymbolLink symbol={symbol} isPreventDefault>
          <div>{numeral(Number(lastPrice)).format('0,0')}</div>
        </SymbolLink>
      </td>
      {renderChange({ change: lastChange, percent: lastPercent })}
    </tr>
  );
};

export default RowWatchlist;
